import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, delay, merge } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  getHook,
  getHookEvents,
  getHookEventsResponse,
  getHookResponse,
  getHooksList,
  getHooksListError,
  getHooksListResponse,
  getHookTypes,
  getHookTypesResponse,
  loadMoreHooksList,
  loadMoreHooksListResponse,
  pingHook,
  pingHookClear,
  pingHookError,
  pingHookResponse,
  removeHook,
  removeHookError,
  removeHookItem,
  removeHookResponse,
  saveHook,
  saveHookError,
  saveHookResponse,
  updateHook,
  updateHookError,
  updateHookResponse,
} from './hooks.actions';
import { HooksResource } from '../../resources/hooks.resource';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../../constants/animation-constants';
import { NotifyService } from '../../../common/services/notify.service';

@Injectable()
export class HooksEffects {
  @Effect()
  getHook$ = this.actions$.pipe(
    ofType(getHook),
    switchMap(({ hookId }) => this.hooksResource.get(hookId).pipe(map((hook) => getHookResponse({ hook })))),
  );

  @Effect()
  getHookTypes$ = this.actions$.pipe(
    ofType(getHookTypes),
    switchMap(() =>
      this.hooksResource.types().pipe(
        map((hookTypes) => getHookTypesResponse({ hookTypes })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getHooksListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  getHookEvents$ = this.actions$.pipe(
    ofType(getHookEvents),
    switchMap(() =>
      this.hooksResource.hookEvents().pipe(
        map((hookEvents) => getHookEventsResponse({ hookEvents })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getHooksListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  getHooks$ = this.actions$.pipe(
    ofType(getHooksList),
    switchMap(({ params }) =>
      this.hooksResource.query(params).pipe(
        map((hooks) => getHooksListResponse({ hooks })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getHooksListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  loadMoreHooks$ = this.actions$.pipe(
    ofType(loadMoreHooksList),
    switchMap(({ params }) =>
      this.hooksResource.query(params).pipe(
        map((hooks) => loadMoreHooksListResponse({ hooks })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getHooksListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  saveHook$ = this.actions$.pipe(
    ofType(saveHook),
    switchMap(({ hook }) =>
      this.hooksResource.save(hook).pipe(
        map((hookResponse) => saveHookResponse({ data: hookResponse })),
        catchError((response) => of(saveHookError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  updateHook$ = this.actions$.pipe(
    ofType(updateHook),
    switchMap(({ hook }) =>
      this.hooksResource.update(hook.id, hook).pipe(
        map((hookResponse) => updateHookResponse({ data: hookResponse })),
        catchError((response) => of(updateHookError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeHook$ = this.actions$.pipe(
    ofType(removeHook),
    switchMap(({ hookId }) =>
      this.hooksResource.remove(hookId).pipe(
        map((hookResponse) => removeHookItem({ data: hookResponse })),
        catchError((response) => of(removeHookError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeHookResponse$ = this.actions$.pipe(
    ofType(removeHookItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removeHookResponse({ data })),
  );

  @Effect()
  pingHook$ = this.actions$.pipe(
    ofType(pingHook),
    switchMap(({ hookId }) =>
      this.hooksResource.ping(hookId).pipe(
        map((hookResponse) => pingHookResponse({ hook: hookResponse })),
        merge(of(pingHookClear({ hookId })).pipe(delay(5000))),
        catchError((response) => of(pingHookError({ error: response.error, hookId }))),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private hooksResource: HooksResource,
    private translate: TranslateService,
    private notify: NotifyService,
  ) {}
}
